// Renders a unified git diff as a collapsible, colorized view so the human
// can judge accept/merge/revert calls from the phone without opening a laptop.
import { el } from "./dom.js";

interface DiffFile {
  path: string;
  adds: number;
  dels: number;
  note: string; // "new file", "deleted", "renamed", "binary" — empty otherwise
  lines: string[]; // hunk headers + content lines, raw
}

// Phones choke on megabyte diffs; past this we stop building DOM and say so.
const MAX_RENDERED_LINES = 2000;

export function diffView(diff: string): HTMLElement {
  const files = parseDiff(diff);
  if (!files.length) {
    // Not a unified diff we understand — show it raw rather than hide it.
    return el("details", { class: "diff" }, [
      el("summary", {}, ["Code changes"]),
      el("div", { class: "diff-body" }, [el("div", { class: "ln" }, [diff])]),
    ]);
  }

  const adds = files.reduce((n, f) => n + f.adds, 0);
  const dels = files.reduce((n, f) => n + f.dels, 0);
  let budget = MAX_RENDERED_LINES;

  const fileViews = files.map((f) => {
    const body = el("div", { class: "diff-body" });
    for (const line of f.lines) {
      if (budget-- <= 0) {
        body.append(el("div", { class: "ln hunk" }, ["… diff too large — showing first lines only"]));
        break;
      }
      body.append(el("div", { class: `ln ${lineClass(line)}` }, [line || " "]));
    }
    return el("details", { class: "diff-file", open: files.length === 1 }, [
      el("summary", {}, [
        el("span", { class: "diff-path" }, [f.path + (f.note ? ` (${f.note})` : "")]),
        stat(f.adds, f.dels),
      ]),
      body,
    ]);
  });

  return el("details", { class: "diff" }, [
    el("summary", {}, [
      "Code changes",
      el("span", { class: "meta" }, [` · ${files.length} file${files.length === 1 ? "" : "s"} `]),
      stat(adds, dels),
    ]),
    ...fileViews,
  ]);
}

function stat(adds: number, dels: number): HTMLElement {
  return el("span", { class: "diff-stat" }, [
    el("span", { class: "plus" }, [`+${adds}`]),
    " ",
    el("span", { class: "minus" }, [`−${dels}`]),
  ]);
}

function lineClass(line: string): string {
  if (line.startsWith("@@")) return "hunk";
  if (line.startsWith("+")) return "add";
  if (line.startsWith("-")) return "del";
  return "";
}

function parseDiff(diff: string): DiffFile[] {
  const files: DiffFile[] = [];
  let cur: DiffFile | null = null;
  let inHunks = false;

  for (const line of diff.split("\n")) {
    if (line.startsWith("diff --git ")) {
      cur = { path: pathFromHeader(line), adds: 0, dels: 0, note: "", lines: [] };
      files.push(cur);
      inHunks = false;
      continue;
    }
    if (!cur) continue;
    if (!inHunks) {
      // File metadata between the header and the first hunk.
      if (line.startsWith("@@")) {
        inHunks = true;
      } else if (line.startsWith("+++ b/")) {
        cur.path = line.slice(6);
        continue;
      } else if (line.startsWith("new file")) {
        cur.note = "new file";
        continue;
      } else if (line.startsWith("deleted file")) {
        cur.note = "deleted";
        continue;
      } else if (line.startsWith("rename ")) {
        cur.note = "renamed";
        continue;
      } else if (line.startsWith("Binary files")) {
        cur.note = "binary";
        continue;
      } else {
        continue; // index lines, mode lines, "--- a/…"
      }
    }
    cur.lines.push(line);
    if (line.startsWith("+") && !line.startsWith("+++")) cur.adds++;
    else if (line.startsWith("-") && !line.startsWith("---")) cur.dels++;
  }
  return files;
}

function pathFromHeader(line: string): string {
  // "diff --git a/some/path b/some/path" — prefer the b/ side.
  const m = line.match(/ b\/(.+)$/);
  return m ? m[1] : line.slice("diff --git ".length);
}
